import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  FinalizeDTO,
  LFOrderList,
  LiveForm,
  LiveFormDTO,
  LiveFormFactory,
  OnlineOrderListDTO,
  OrderData,
  OrderDTO,
  OrderFactory
} from '@lfw/roesberg.liveforms.core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProjectsService } from '../projects/projects.service';
import { AppUrls } from '../route/app-urls';
import { ErrorHandlers } from '../shared/helpers/error-handlers';

@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  private readonly orderLoadError = '';

  private readonly projectsService = inject(ProjectsService);
  private readonly http = inject(HttpClient);

  getOnlineOrders(): Observable<LFOrderList> {
    return this.http
      .get<OnlineOrderListDTO>(
        AppUrls.getApiUrl(AppUrls.urlOrders, 'getonlineordersforproject') + this.formatParams('', '')
      )
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoRaw) => {
          return OrderFactory.fromOnlineOrderDTOList(dtoRaw, this.projectsService.currentProjectId);
        }),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  getOnlineOrder(id: string): Observable<OrderData> {
    return this.http
      .get<OrderDTO>(AppUrls.getApiUrl(AppUrls.urlOrders, 'getonlineorder') + this.formatParams('orderid', id))
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoRaw) => {
          return OrderFactory.fromOrderDTO(dtoRaw);
        }),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  getReport(id: string): Observable<Blob> {
    return this.http
      .get(AppUrls.getApiUrl(AppUrls.urlOrders, 'getreport') + this.formatParams('orderid', id), {
        responseType: 'blob'
      })
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  getManual(): Observable<Blob> {
    return this.http.get(AppUrls.getApiUrl(AppUrls.urlDocument, 'getmanual'), {
      responseType: 'blob'
    });
  }

  finishOrder(projectId: string, orderId: string): Observable<OrderData> {
    const dto: FinalizeDTO = {
      projectId: projectId,
      orderId: orderId
    };

    return this.http.post<OrderDTO>(AppUrls.getApiUrl(AppUrls.urlOrders, 'finishorder'), JSON.stringify(dto)).pipe(
      //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
      map((dtoRaw) => {
        return OrderFactory.fromOrderDTO(dtoRaw);
      }),
      catchError((error) => {
        return ErrorHandlers.errorHandler(error);
      })
    );
  }

  pauseOrder(projectId: string, orderId: string): Observable<OrderData> {
    const dto: FinalizeDTO = {
      projectId: projectId,
      orderId: orderId
    };

    return this.http.post<OrderDTO>(AppUrls.getApiUrl(AppUrls.urlOrders, 'pauseorder'), JSON.stringify(dto)).pipe(
      //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
      map((dtoRaw) => {
        return OrderFactory.fromOrderDTO(dtoRaw);
      }),
      catchError((error) => {
        return ErrorHandlers.errorHandler(error);
      })
    );
  }

  checkoutOrder(projectId: string, orderId: string): Observable<LiveForm> {
    const dto: FinalizeDTO = {
      projectId: projectId,
      orderId: orderId
    };

    return this.http.post<LiveFormDTO>(AppUrls.getApiUrl(AppUrls.urlOrders, 'checkoutorder'), JSON.stringify(dto)).pipe(
      //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
      map((dtoRaw) => {
        return LiveFormFactory.fromLiveFormDTO(dtoRaw);
      }),
      catchError((error) => {
        return ErrorHandlers.errorHandler(error);
      })
    );
  }

  discardCheckedOutOrder(projectId: string, orderId: string): Observable<void> {
    const dto: FinalizeDTO = { projectId, orderId };
    return this.http
      .post<void>(AppUrls.getApiUrl(AppUrls.urlOrders, 'discardcheckedoutorder'), JSON.stringify(dto))
      .pipe(catchError((error) => ErrorHandlers.errorHandler(error)));
  }

  private formatParams(name: string, value: string): string {
    let param = '?projectid=' + this.projectsService.currentProjectId;
    if (name != null && name.length > 0 && value != null && value.length > 0) {
      // encodeURIComponent is needed to handle special characters like # for projectids in the tree
      param = param + '&' + name + '=' + encodeURIComponent(value);
    }
    return param;
  }

  get orderSelectErrorCode(): string {
    return this.orderLoadError.split(':')[0];
  }
}
